import { ComponentType, lazy, LazyExoticComponent } from 'react';
import React from 'react';
import { Poll } from 'schema/poll';
import { QuestionProps } from 'src/types/poll.domain';
import { createPollRenderComponent } from '../root/poll';
import { WrapperProps } from '../root/wrapper';
import { Final } from './finals/Final';
import {
  asyncCompositeQuestionComponentLoaders as compositeLoaders,
  asyncQuestionComponentLoaders as loaders,
} from './questionLoaders';
import { Welcome } from './welcomes/Welcome';
import { WelcomeMobileStories } from './welcomes/WelcomeMobileStories';
import { QuestionWrapper } from './Wrappers';
import { GroupQuestionWrapper as NewGroupQuestionWrapper } from './Wrappers/GroupQuestionWrapper';
import { GroupWrapper as NewGroupWrapper } from './Wrappers/GroupWrapper';
import { MatrixQuestionWrapper as NewMatrixQuestionWrapper } from './Wrappers/MatrixQuestionWrapper';
import { MatrixWrapper as NewMatrixWrapper } from './Wrappers/MatrixWrapper';

export { trackingEngine } from './pollTrackingEngine';
export { pollEngine as schemaEngine } from 'schema';

const makeLazy = <T,>(loader: () => Promise<React.ComponentType<T>>): LazyExoticComponent<ComponentType<T>> =>
  lazy(async () => {
    const loaderPromise = loader();
    lastLoadPromise = Promise.all([loaderPromise, lastLoadPromise]);
    const Component = await loaderPromise;
    // this.questionsMapping[config.name]!.component = Component;
    return { default: Component };
  });

const questionsMapping: Record<
  Exclude<Poll['questions'][number]['data']['type'], 'POLL_CARD_SORTING_CATEGORY'>,
  React.FC<QuestionProps & { groupId?: string | undefined } & Omit<WrapperProps, 'children'>>
> = {
  MEDIA_FILE_AUDIO: makeLazy(loaders.MEDIA_FILE_AUDIO),
  MEDIA_FILE_TEXT: makeLazy(loaders.MEDIA_FILE_TEXT),
  MEDIA_FILE_VIDEO: makeLazy(loaders.MEDIA_FILE_VIDEO),
  POLL_TEXTAREA: makeLazy(loaders.POLL_TEXTAREA),
  POLL_PROTOTYPE_SCREEN: makeLazy(loaders.POLL_PROTOTYPE_SCREEN),
  POLL_SINGLE: makeLazy(loaders.POLL_SINGLE),
  POLL_MAXDIFF: makeLazy(loaders.POLL_MAXDIFF),
  POLL_CHOICE_CONJOINT: makeLazy(loaders.POLL_CHOICE_CONJOINT),
  POLL_MULTIPLE: makeLazy(loaders.POLL_MULTIPLE),
  POLL_SINGLE_FROM_PICTURE: makeLazy(loaders.POLL_SINGLE_FROM_PICTURE),
  POLL_MULTIPLE_FROM_PICTURE: makeLazy(loaders.POLL_MULTIPLE_FROM_PICTURE),
  POLL_RANK: makeLazy(loaders.POLL_RANK),
  POLL_RATING_DIGITS: makeLazy(loaders.POLL_RATING_DIGITS),
  POLL_RATING_LIKE: makeLazy(loaders.POLL_RATING_LIKE),
  POLL_RATING_SLIDER: makeLazy(loaders.POLL_RATING_SLIDER),
  POLL_RATING_SMILIES: makeLazy(loaders.POLL_RATING_SMILIES),
  POLL_RATING_STARS: makeLazy(loaders.POLL_RATING_STARS),
  POLL_SELECT: makeLazy(loaders.POLL_SELECT),
  POLL_MULTIPLE_SELECT: makeLazy(loaders.POLL_MULTIPLE_SELECT),
  MATRIX_POLL_SINGLE: makeLazy(loaders.MATRIX_POLL_SINGLE),
  MATRIX_POLL_MULTIPLE: makeLazy(loaders.MATRIX_POLL_MULTIPLE),
  MATRIX_POLL_RATING_DIGITS: makeLazy(loaders.MATRIX_POLL_RATING_DIGITS),
  MATRIX_POLL_RATING_STARS: makeLazy(loaders.MATRIX_POLL_RATING_STARS),
  POLL_CLICK_MAP: makeLazy(loaders.POLL_CLICK_MAP),
  POLL_MARKUP_AREA: makeLazy(loaders.POLL_MARKUP_AREA),
  POLL_SELECT_AREA: makeLazy(loaders.POLL_SELECT_AREA),
  POLL_FILE: makeLazy(loaders.POLL_FILE),
  MEDIA_FILE_ANY: makeLazy(loaders.MEDIA_FILE_ANY),
  MEDIA_FILE_IMAGE: makeLazy(loaders.MEDIA_FILE_IMAGE),
  MEDIA_FILE_LINK: makeLazy(loaders.MEDIA_FILE_LINK),
  POLL_AUTH: makeLazy(loaders.POLL_AUTH),
  POLL_SWITCH: makeLazy(loaders.POLL_SWITCH),
  POLL_BINARY: makeLazy(loaders.POLL_BINARY),
};

let lastLoadPromise: Promise<unknown> = Promise.resolve();

export const component = createPollRenderComponent<Poll>({
  QuestionWrapper,
  InnerGroupWrapper: props => {
    const InnerWrapper = props.deck?.type === 'matrix' ? NewMatrixQuestionWrapper : NewGroupQuestionWrapper;
    return <InnerWrapper {...props} />;
  },
  OuterGroupWrapper: props => {
    const OuterWrapper = props.deck?.type === 'matrix' ? NewMatrixWrapper : NewGroupWrapper;
    return <OuterWrapper {...props} />;
  },
  Questions: questionsMapping,
  CompositeQuestions: {
    POLL_CARD_SORTING_CATEGORY: makeLazy(compositeLoaders.POLL_CARD_SORTING_CATEGORY),
  },
  Welcome: {
    WELCOME_MOBILE_STORIES: WelcomeMobileStories,
    WELCOME_SIMPLE: Welcome,
  },
  Final: {
    FINAL_CLICK_PAGE: Final,
  },
  async preloadQuestion(type: string): Promise<void> {
    const loader = loaders[type as keyof typeof questionsMapping];
    const Component = await lastLoadPromise.then(() => loader());
    questionsMapping[type as keyof typeof questionsMapping] = Component;
  },
});
